<template>
  <div class="row ka">
    <!-- Live kiosk of the chosen sale channel (kiosk preview mode, KSK063). -->
    <div class="col-lg-5 col-12 d-flex justify-content-center mb-3">
      <div class="ka-frame">
        <iframe
          v-if="previewUrl"
          :key="previewUrl"
          ref="frame"
          :src="previewUrl"
          class="ka-iframe"
          title="Kiosk preview"
        ></iframe>
        <div v-else class="ka-empty">{{ $t("Kiosk_Select_Channel") }}</div>
      </div>
    </div>

    <div class="col-lg-7 col-12">
      <div class="row align-items-center mb-3">
        <div class="col-lg-4 col-12 label-form">Sale Channel</div>
        <div class="col-lg-8 col-12">
          <Multiselect
            :searchable="true"
            :model-value="branchId"
            :options="branchs"
            :canClear="false"
            @change="(id) => $emit('change-branch', id)"
          />
        </div>
      </div>

      <p class="ka-hint">{{ $t("Kiosk_Appearance_Hint") }}</p>

      <div class="ka-toolbar">
        <div class="ka-segment">
          <button
            v-for="page in pages"
            :key="page.key"
            type="button"
            class="ka-segment__btn"
            :class="{ 'is-active': currentPage === page.key }"
            :disabled="!ready"
            @click="navigate(page.key)"
          >
            {{ $t(page.label) }}
          </button>
        </div>
        <div class="ka-segment">
          <button
            type="button"
            class="ka-segment__btn"
            :class="{ 'is-active': pickMode }"
            :disabled="!ready"
            @click="setMode(true)"
          >
            {{ $t("Kiosk_Pick_Colours") }}
          </button>
          <button
            type="button"
            class="ka-segment__btn"
            :class="{ 'is-active': !pickMode }"
            :disabled="!ready"
            @click="setMode(false)"
          >
            {{ $t("Kiosk_Use_Kiosk") }}
          </button>
        </div>
      </div>

      <!-- KSK117: plain background colours; unset = the kiosk's current background. -->
      <div class="ka-card mb-3">
        <div class="ka-card__title">{{ $t("Kiosk_Background_Colours") }}</div>
        <div v-for="bg in backgrounds" :key="bg.key" class="ka-row">
          <span class="ka-row__label">{{ $t(bg.label) }}</span>
          <color-input
            :model-value="bgValue(bg.key) || '#ffffff'"
            disable-alpha
            position="left bottom"
            @update:modelValue="setBackground(bg.key, $event)"
          />
          <input
            :value="bgValue(bg.key)"
            type="text"
            class="ka-part__hex"
            maxlength="7"
            :placeholder="$t('Kiosk_Bg_Default')"
            spellcheck="false"
            :aria-label="$t(bg.label) + ' hex'"
            @input="onBgHexInput(bg.key, $event.target.value)"
            @blur="$event.target.value = bgValue(bg.key)"
          />
          <button
            type="button"
            class="ka-icon-btn"
            :disabled="!bgValue(bg.key)"
            :aria-label="$t('Kiosk_Bg_Reset')"
            :title="$t('Kiosk_Bg_Reset')"
            @click="setBackground(bg.key, '')"
          >
            <i class="material-icons-round">restart_alt</i>
          </button>
        </div>
      </div>

      <div class="ka-card">
        <div class="ka-card__title">{{ $t("Kiosk_Custom_Colours") }}</div>
        <div v-if="!customSlots.length" class="ka-muted">{{ $t("Kiosk_No_Custom_Colours") }}</div>
        <div v-for="slot in customSlots" :key="slot" class="ka-row">
          <span class="ka-row__label">{{ slotLabel(slot) }}</span>
          <span class="ka-row__swatches">
            <span
              v-for="part in parts"
              :key="part"
              class="ka-swatch"
              :class="{ 'ka-swatch--none': !colors[slot][part] }"
              :style="colors[slot][part] ? { background: colors[slot][part] } : null"
              :title="$t(partLabel(part)) + (colors[slot][part] ? ': ' + colors[slot][part] : '')"
            ></span>
          </span>
          <button type="button" class="ka-icon-btn" :aria-label="'Edit ' + slotLabel(slot)" @click="openPopup(slot)">
            <i class="material-icons-round">edit</i>
          </button>
          <button type="button" class="ka-icon-btn" :aria-label="'Use theme colour for ' + slotLabel(slot)" @click="removeSlot(slot)">
            <i class="material-icons-round">restart_alt</i>
          </button>
        </div>
      </div>

      <div v-if="dirty" class="ka-unsaved">{{ $t("Kiosk_Unsaved") }}</div>

      <div class="d-flex flex-wrap gap-2 mt-3">
        <material-button
          class="btn-background btn-sm"
          :disabled="!branchId || saving"
          @click="save()"
        >
          {{ $t("Save") }}
        </material-button>
        <material-button
          variant="outline"
          class="btn-sm"
          :disabled="!Object.keys(colors).length"
          @click="resetAll()"
        >
          {{ $t("Kiosk_Reset_All") }}
        </material-button>
      </div>
    </div>

    <!-- Colour popup for one kiosk element -->
    <div v-if="popup.open" class="ka-overlay" @click.self="popup.open = false">
      <div class="ka-popup" role="dialog" aria-modal="true">
        <h5 class="ka-popup__title">{{ slotLabel(popup.slot) }}</h5>
        <label class="ka-radio">
          <input v-model="popup.custom" type="radio" :value="false" />
          {{ $t("Kiosk_Use_Theme_Colour") }}
        </label>
        <label class="ka-radio">
          <input v-model="popup.custom" type="radio" :value="true" />
          {{ $t("Kiosk_Set_Custom_Colour") }}
        </label>
        <div v-if="popup.custom" class="ka-popup__parts">
          <div v-for="part in parts" :key="part" class="ka-part">
            <span class="ka-part__label">{{ $t(partLabel(part)) }}</span>
            <color-input v-model="popup.values[part]" disable-alpha position="left bottom" />
            <input
              :value="popup.values[part]"
              type="text"
              class="ka-part__hex"
              maxlength="7"
              placeholder="#000000"
              spellcheck="false"
              :aria-label="$t(partLabel(part)) + ' hex'"
              @input="onHexInput(part, $event.target.value)"
              @blur="$event.target.value = popup.values[part]"
            />
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-4">
          <material-button variant="outline" class="btn-sm" @click="popup.open = false">
            {{ $t("Kiosk_Cancel") }}
          </material-button>
          <material-button class="btn-background btn-sm" @click="applyPopup()">
            {{ $t("Kiosk_Apply") }}
          </material-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// KSK063 — per-element kiosk colours. The kiosk itself runs in the iframe in
// preview mode (?ksk_preview=1, minty.crm.onlineordering kioskPreview.js):
// it reports the element the merchant clicked and repaints with the colours
// posted back. Nothing is saved until Save.
import Multiselect from "@vueform/multiselect";
import ColorInput from "vue-color-input";
import MaterialButton from "@/components/MaterialButton.vue";
import SettingGeneralService from "../../../services/settingGeneral.service";

const PARTS = ["outline", "font", "accent"];
const PART_LABELS = { outline: "Kiosk_Outline", font: "Kiosk_Font", accent: "Kiosk_Accent" };
const HEX = /^#[0-9a-f]{6}$/i;
// KSK117 — kiosk background colours, stored in the same JSON as
// { key: { accent } } (minty.crm.onlineordering KIOSK_BACKGROUND_SLOTS).
const BACKGROUNDS = [
  { key: "bg_header", label: "Kiosk_Bg_Header" },
  { key: "bg_category", label: "Kiosk_Bg_Category" },
  { key: "bg_menu", label: "Kiosk_Bg_Menu" },
];
const BACKGROUND_KEYS = BACKGROUNDS.map((bg) => bg.key);

export default {
  name: "KioskAppearance",
  components: { Multiselect, ColorInput, MaterialButton },
  emits: ["change-branch", "saved", "notify"],
  props: {
    branchs: { type: Array, default: () => [] },
    branchId: { type: [Number, String], default: 0 },
    mooOrderUrl: { type: String, default: "" },
    savedColors: { type: Object, default: null },
  },
  data() {
    return {
      parts: PARTS,
      backgrounds: BACKGROUNDS,
      pages: [
        { key: "landing", label: "Kiosk_Page_Landing" },
        { key: "menu", label: "Kiosk_Page_Menu" },
        { key: "cart", label: "Kiosk_Page_Cart" },
        { key: "payment", label: "Kiosk_Page_Payment" },
        { key: "rewards", label: "Kiosk_Page_Rewards" },
      ],
      colors: {},
      slotLabels: {},
      ready: false,
      pickMode: true,
      currentPage: "landing",
      dirty: false,
      saving: false,
      popup: { open: false, slot: "", custom: false, values: {} },
    };
  },
  computed: {
    previewUrl() {
      return this.mooOrderUrl ? `${this.mooOrderUrl}/kiosk?ksk_preview=1` : "";
    },
    previewOrigin() {
      try {
        return new URL(this.mooOrderUrl).origin;
      } catch (e) {
        return "";
      }
    },
    customSlots() {
      return Object.keys(this.colors).filter((slot) => !BACKGROUND_KEYS.includes(slot));
    },
  },
  watch: {
    savedColors: {
      immediate: true,
      handler(value) {
        this.colors = this.clean(value);
        this.dirty = false;
        this.pushColors();
      },
    },
    // Another sale channel: drop unsaved edits even when both have no saved
    // colours (savedColors stays null, so its watcher does not fire).
    branchId() {
      this.popup.open = false;
      this.colors = this.clean(this.savedColors);
      this.dirty = false;
      this.pushColors();
    },
    previewUrl() {
      this.ready = false;
      this.pickMode = true;
      this.currentPage = "landing";
    },
  },
  mounted() {
    window.addEventListener("message", this.onMessage);
  },
  beforeUnmount() {
    window.removeEventListener("message", this.onMessage);
  },
  methods: {
    clean(value) {
      const out = {};
      Object.keys(value || {}).forEach((slot) => {
        const parts = {};
        PARTS.forEach((part) => {
          const color = value[slot] && value[slot][part];
          if (HEX.test(color || "")) parts[part] = color.toLowerCase();
        });
        if (Object.keys(parts).length) out[slot] = parts;
      });
      return out;
    },
    slotLabel(slot) {
      return this.slotLabels[slot] || slot;
    },
    partLabel(part) {
      return PART_LABELS[part];
    },
    send(msg) {
      const frame = this.$refs.frame;
      if (!frame || !frame.contentWindow || !this.previewOrigin) return;
      // Plain copy: a reactive Proxy cannot be structured-cloned.
      frame.contentWindow.postMessage(JSON.parse(JSON.stringify({ target: "ksk-preview", ...msg })), this.previewOrigin);
    },
    pushColors() {
      if (this.ready) this.send({ type: "colors", colors: Object.keys(this.colors).length ? this.colors : null });
    },
    onMessage(e) {
      const frame = this.$refs.frame;
      if (!frame || e.source !== frame.contentWindow || e.origin !== this.previewOrigin) return;
      const msg = e.data || {};
      if (msg.source !== "ksk-preview") return;
      if (msg.type === "ready") {
        this.ready = true;
        this.slotLabels = msg.slots || {};
        this.pushColors();
        this.send({ type: "mode", pick: this.pickMode });
      } else if (msg.type === "select") {
        if (msg.label) this.slotLabels = { ...this.slotLabels, [msg.slot]: msg.label };
        this.openPopup(msg.slot, msg.current);
      }
    },
    navigate(page) {
      this.currentPage = page;
      this.send({ type: "navigate", page });
    },
    setMode(pick) {
      this.pickMode = pick;
      this.send({ type: "mode", pick });
    },
    openPopup(slot, current) {
      const saved = this.colors[slot];
      const values = {};
      PARTS.forEach((part) => {
        values[part] = (saved && saved[part]) || (current && current[part]) || "#000000";
      });
      this.popup = { open: true, slot, custom: !!saved, values };
    },
    // KSK109 — typed hex only lands once it is a full #rrggbb; a half-typed
    // value stays in the box and is reverted on blur.
    onHexInput(part, value) {
      const hex = (value || "").trim().replace(/^#?/, "#");
      if (HEX.test(hex)) this.popup.values[part] = hex.toLowerCase();
    },
    applyPopup() {
      const { slot, custom, values } = this.popup;
      const next = { ...this.colors };
      if (custom) {
        const parts = this.clean({ [slot]: values })[slot];
        if (parts) next[slot] = parts;
        else delete next[slot];
      } else {
        delete next[slot];
      }
      this.colors = next;
      this.popup.open = false;
      this.dirty = true;
      this.pushColors();
    },
    bgValue(key) {
      return (this.colors[key] && this.colors[key].accent) || "";
    },
    setBackground(key, color) {
      const next = { ...this.colors };
      if (HEX.test(color || "")) next[key] = { accent: color.toLowerCase() };
      else delete next[key];
      this.colors = next;
      this.dirty = true;
      this.pushColors();
    },
    onBgHexInput(key, value) {
      const hex = (value || "").trim().replace(/^#?/, "#");
      if (HEX.test(hex)) this.setBackground(key, hex);
    },
    removeSlot(slot) {
      const next = { ...this.colors };
      delete next[slot];
      this.colors = next;
      this.dirty = true;
      this.pushColors();
    },
    resetAll() {
      this.colors = {};
      this.dirty = true;
      this.pushColors();
    },
    save() {
      this.saving = true;
      SettingGeneralService.updateKioskAppearance({
        branch_id: this.branchId,
        kiosk_custom_colors: this.colors,
      }).then(
        (response) => {
          this.saving = false;
          if (response.data.result.isSuccess) {
            this.dirty = false;
            this.$emit("saved", response.data.data.kiosk_custom_colors || null);
            this.$emit("notify", "success", this.$t("Kiosk_Saved"));
          } else {
            this.$emit("notify", "danger", response.data.result.message);
          }
        },
        (error) => {
          this.saving = false;
          this.$emit("notify", "danger", error);
        }
      );
    },
  },
};
</script>

<style scoped>
.ka-frame {
  width: 405px;
  height: 720px;
  max-width: 100%;
  border: 1px solid #dee2e6;
  border-radius: 16px;
  overflow: hidden;
  background: #f5f5f5;
}
.ka-iframe {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}
.ka-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  color: #6c757d;
}
.ka-hint {
  margin-bottom: 12px;
  color: #6c757d;
}
.ka-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}
.ka-segment {
  display: inline-flex;
  border: 1px solid #ced4da;
  border-radius: 8px;
  overflow: hidden;
}
.ka-segment__btn {
  padding: 6px 14px;
  border: 0;
  border-right: 1px solid #ced4da;
  background: #fff;
  font-size: 14px;
  cursor: pointer;
}
.ka-segment__btn:last-child {
  border-right: 0;
}
.ka-segment__btn.is-active {
  background: #344767;
  color: #fff;
}
.ka-segment__btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.ka-card {
  border: 1px solid #dee2e6;
  border-radius: 12px;
  padding: 16px;
}
.ka-card__title {
  margin-bottom: 8px;
  font-weight: 600;
}
.ka-muted {
  color: #6c757d;
}
.ka-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
  border-top: 1px solid #f1f3f5;
}
.ka-row:first-of-type {
  border-top: 0;
}
.ka-row__label {
  flex: 1;
}
.ka-row__swatches {
  display: inline-flex;
  gap: 6px;
}
.ka-swatch {
  width: 22px;
  height: 22px;
  border: 1px solid #ced4da;
  border-radius: 6px;
}
.ka-swatch--none {
  background: repeating-linear-gradient(45deg, #fff 0 4px, #e9ecef 4px 8px);
}
.ka-icon-btn {
  border: 0;
  background: transparent;
  color: #344767;
  cursor: pointer;
  padding: 2px;
}
.ka-icon-btn:disabled {
  cursor: default;
  opacity: 0.35;
}
.ka-unsaved {
  margin-top: 12px;
  color: #b45309;
}
.ka-overlay {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
}
.ka-popup {
  width: 420px;
  max-width: calc(100% - 32px);
  padding: 24px;
  border-radius: 16px;
  background: #fff;
}
.ka-popup__title {
  margin-bottom: 16px;
}
.ka-radio {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  cursor: pointer;
}
.ka-popup__parts {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.ka-part {
  display: flex;
  align-items: center;
  gap: 12px;
}
.ka-part__label {
  width: 80px;
}
.ka-part__hex {
  width: 96px;
  padding: 4px 8px;
  border: 1px solid #333333;
  border-radius: 6px;
  background: #333333;
  font-family: monospace;
  color: #ffffff;
}
.ka-part__hex::placeholder {
  color: rgba(255, 255, 255, 0.6);
}
.ka-part__hex:focus {
  outline: none;
  border-color: #ffffff;
}
</style>
